import { Grid } from "@mui/material";
import {  useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useLeadChartDistribution } from "../../services/leadsService";
import TableService from "../../services/tableService";
import { LEADS_TABLE_NAME, useLeadsTableFieldsConfig } from "../LeadsPage/LeadsPageTableConfig";
import ChartService from "../../services/chartService";
import VerticalBarChart from "../../components/charts/VerticalBarChart/VerticalBarChart";
import LeadReportsNavigationBar from "./LeadChartsNavigationBar";
import LeadChartToolBar from "./LeadChartToolBar";
import { useAccessControl } from "../../hooks/auth/useCheckPermission";
import { USER_ROLES } from "../../helpers/constants";

export default function BaseLeadChartPage(props) {
    const { hasAccess } = useAccessControl([USER_ROLES.SALES_MARKETING, USER_ROLES.ADMIN]);

    if (!hasAccess) {
        return null;
    }

    const { pageName, groupByKey, labelByKey, showTotalPercentedRatio, showPercentedRatio } = props;

    const LEADS_TABLE_FIELDS_CONFIG = useLeadsTableFieldsConfig();

    const { t } = useTranslation();

    const [dataLocal, setDataLocal] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [leadDistribution, setLeadDistribution] = useState()

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const leadDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            LEADS_TABLE_FIELDS_CONFIG, // @ts-ignore
           [],
           []
        );
    }, [
        LEADS_TABLE_FIELDS_CONFIG,
    ]);

    // const tempSearchParams = new URLSearchParams({...Object.fromEntries(searchParams.entries()), group_by_key: groupByKey, label_by_key: labelByKey})
    const tempSearchParams = searchParams;
    tempSearchParams.set("group_by_key", groupByKey)
    tempSearchParams.set("label_by_key", labelByKey)
    const { data: leadsRows, refetch, isLoading } = useLeadChartDistribution(tempSearchParams);
    useEffect(() => {
        if (leadsRows?.results) {
            setDataLocal(leadsRows.results);
            setRecordsCount(leadsRows.count);
            setLeadDistribution(leadsRows.lead_distribution)
        }
    }, [leadsRows, dataPage, dataPageSize]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                LEADS_TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[LEADS_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[LEADS_TABLE_NAME]
                    : [], // @ts-ignore
                // onClickButton
                columnsOrdersForTables?.[LEADS_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[LEADS_TABLE_NAME]
                    : [],
            ),
        [
            LEADS_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            // onClickButton
        ],
    );

    useEffect(() => {
        refetch();
    }, [searchParams]);

    if (!leadDistribution) {
        return <LoaderWrapper showLoader={true}></LoaderWrapper>
    }
   
    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.lead_reports")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={12}>
                    <LeadReportsNavigationBar />
                </Grid>
                <Grid item xs={12}>
                    <LeadChartToolBar pageName={pageName}/>
                </Grid>
                <Grid
                  item
                  xs={12}
                  style={{
                    height: "60vh"
                  }}
                >
                  <VerticalBarChart
                    data={{
                      labels: leadDistribution?.labels,
                      datasets:ChartService.prepareDataForSingleOrMultiGroupsChart(
                          leadDistribution?.data,
                          leadDistribution?.is_multiple_groups_chart,
                        ),
                    }}
                    legendDisplay={leadDistribution?.is_multiple_groups_chart}
                    titleDisplay={false}
                    dataLabelsDisplay={true}
                    tooltipFormaterFn={(value, context) =>
                      showTotalPercentedRatio
                        ? ChartService.getChartTooltipFormaterForTotalPercentRatioFn(
                            value,
                            recordsCount
                        ) :
                        showPercentedRatio ?
                        ChartService.getChartTooltipFormaterForPercentRatioFn(
                            value,
                            context
                        ) : 
                        undefined
                    }
                    datalabelsFormaterFn= {(value, context) =>
                        showTotalPercentedRatio ?
                        ChartService.getChartDataLabelsForTotalPercentRatioFn(
                            context,
                            recordsCount
                        ) : 
                        showPercentedRatio ?
                        ChartService.getChartDataLabelsForPercentRatioFn(
                            value,
                            context,
                        ) : 
                        undefined
                    }     
                  />
                </Grid>
                <Grid item xs={12}>
                        <FilterTable
                            // @ts-ignore
                            data={data}
                            countRecords={recordsCount}
                            resetPageNumber={resetPageNumber}
                            style={{ maxHeight: "60vh" }}
                            headersConfig={TableService.orderColumns(
                                TableService.hideHiddenColumns(
                                    LEADS_TABLE_FIELDS_CONFIG, // @ts-ignore
                                    hiddenColumnsForTables?.[LEADS_TABLE_NAME]
                                        ? // @ts-ignore
                                          hiddenColumnsForTables?.[LEADS_TABLE_NAME]
                                        : [],
                                ), // @ts-ignore
                                columnsOrdersForTables?.[LEADS_TABLE_NAME]
                                    ? // @ts-ignore
                                      columnsOrdersForTables?.[LEADS_TABLE_NAME]
                                    : [],
                            )}
                            showCleanFilterIcon={true}
                            showContextMenu={false}
                            showExportToFileButton={true}
                            exportToFileUrl={"sales/leads/export/"}
                            exportToFileSearchParams={searchParams}
                            exportToFileHeaders={leadDataExportHeaders}
                            exportToFileFileName={`${t("lead.exportFileName")}.xlsx`}
                            showDetailsIcon={false}
                            showCheckbox={false}
                            page={dataPage}
                            onPageChange={handleChangeDataPage}
                            rowsPerPage={dataPageSize}
                            onRowsPerPageChange={handleChangeDataPageSize}
                            setDataLocal={setDataLocal}
                            hideFiltersIconCol={true}
                            isLoading={isLoading}
                        />
                    
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
